import fetchWithCsrf from 'js/utils/fetch_with_csrf';
import routes from 'sitewide-js/core_pre_webpack/routes';

export interface AiWorkPoster {
  id: number;
  work_id: number;
  user_id: number;
  content_url?: string;
  visibility?: string;
  metadata?: Record<string, unknown>;
  generation_started_at?: string;
  generation_completed_at?: string;
  failed_at?: string;
  failure_reason?: string;
  created_at: string;
  updated_at: string;
}

export interface AiProductFeedback {
  id: number;
  rating?: number;
  comment?: string;
  completed_at?: number;
}

export interface AiProductFeedbackResponse {
  success: boolean;
  ai_product_feedback: AiProductFeedback;
}

export interface GetPosterResponse {
  success: boolean;
  ai_work_poster: AiWorkPoster;
  error?: string;
}

export interface DownloadPosterResponse {
  success: boolean;
  download_url: string;
  error?: string;
}

// Fetch poster for a specific work
export const getWorkPoster = async (
  workId: number,
): Promise<GetPosterResponse> => {
  const response = await fetchWithCsrf(
    routes.api_v0_ai_work_poster_path({
      subdomain_param: 'api',
      work_id: workId,
    }),
    {
      method: 'GET',
    },
  );

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || 'Failed to fetch poster');
  }

  return response.json();
};

// Download poster PPTX file
export const downloadPoster = async (
  workId: number,
): Promise<DownloadPosterResponse> => {
  const response = await fetchWithCsrf(
    routes.api_v0_download_ai_work_poster_path({
      subdomain_param: 'api',
      work_id: workId,
    }),
    {
      method: 'GET',
    },
  );

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || 'Failed to get download URL');
  }

  return response.json();
};

// Reuse existing AI product feedback functions
export const getOrCreateAiProductFeedback = async (
  workId: number,
  productType: string,
): Promise<AiProductFeedbackResponse> => {
  const response = await fetchWithCsrf(
    routes.api_v0_ai_product_feedback_get_or_create_product_feedback_path({
      subdomain_param: 'api',
    }),
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        work_id: workId,
        product_type: productType,
      }),
    },
  );

  if (!response.ok) {
    throw new Error('Failed to get or create AI product feedback');
  }

  return response.json();
};

export const updateAiProductFeedback = async (params: {
  id: number;
  rating?: number;
  comment?: string;
  completed_at?: number;
}): Promise<void> => {
  const response = await fetchWithCsrf(
    routes.api_v0_ai_product_feedback_update_product_feedback_path({
      subdomain_param: 'api',
      id: params.id,
    }),
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(params),
    },
  );

  if (!response.ok) {
    throw new Error('Failed to update AI product feedback');
  }
};

// Upload poster image to create Photo record
export const uploadPosterImage = async (
  workId: number,
): Promise<{ success: boolean; photo: { id: string; url: string } }> => {
  const response = await fetchWithCsrf(
    routes.api_v0_ai_work_poster_upload_image_path({
      subdomain_param: 'api',
      work_id: workId,
    }),
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || 'Failed to upload poster image');
  }

  return response.json();
};
